/**
 * Risk calculation logic using RiskData matrices.
 * Uses SBP bands (rows), Non-HDL bands (cols), age band, gender, smoker.
 * SCORE2-OP: applies diabetes multiplier when diabetes = true.
 */
import {
  SCORE2_MATRICES,
  SCORE2_OP_MATRICES,
  SCORE2_AGE_BANDS,
  SCORE2_OP_AGE_BANDS,
  SCORE2_OP_DIABETES_MULTIPLIER,
  type Gender,
  type RiskMatrix,
} from './RiskData';

export type CalculatorMode = 'SCORE2' | 'SCORE2-OP';

export interface CalculationInput {
  mode: CalculatorMode;
  gender: Gender;
  age: number;
  smoker: boolean;
  systolicBp: number;
  totalCholesterol: number;
  hdlCholesterol: number;
  diabetes?: boolean; // only for SCORE2-OP
}

export interface CalculationResult {
  riskPercent: number;
  hasDiabetesMultiplier: boolean;
  rawRiskPercent?: number;
}

/**
 * Get SBP row index (0-3). SBP outside 100-179 is clamped to nearest band.
 */
function getSbpRowIndex(sbp: number): number {
  if (sbp < 100) return 0;
  if (sbp <= 119) return 0;
  if (sbp <= 139) return 1;
  if (sbp <= 159) return 2;
  return 3; // 160-179 or higher clamped
}

/**
 * Get Non-HDL column index (0-3).
 */
function getNonHdlColIndex(nonHdl: number): number {
  if (nonHdl < 155) return 0;
  if (nonHdl <= 190) return 1;
  if (nonHdl <= 230) return 2;
  return 3;
}

/**
 * Get SCORE2 age band index (0-5) for age 40-69.
 */
function getScore2AgeBandIndex(age: number): number {
  for (let i = 0; i < SCORE2_AGE_BANDS.length; i++) {
    if (age >= SCORE2_AGE_BANDS[i].min && age <= SCORE2_AGE_BANDS[i].max) {
      return i;
    }
  }
  if (age < 40) return 0;
  return 5; // 65-69
}

/**
 * Get SCORE2-OP age band index (0-4) for age 70-110.
 */
function getScore2OpAgeBandIndex(age: number): number {
  for (let i = 0; i < SCORE2_OP_AGE_BANDS.length; i++) {
    if (age >= SCORE2_OP_AGE_BANDS[i].min && age <= SCORE2_OP_AGE_BANDS[i].max) {
      return i;
    }
  }
  if (age < 70) return 0;
  return 4; // 90-110
}

/**
 * Calculate 10-year CVD risk from inputs.
 * Non-HDL = Total Cholesterol - HDL Cholesterol (calculated internally).
 */
export function calculateRisk(input: CalculationInput): CalculationResult | null {
  const nonHdl = input.totalCholesterol - input.hdlCholesterol;
  if (nonHdl < 0) return null;

  const sbpRow = getSbpRowIndex(input.systolicBp);
  const nonHdlCol = getNonHdlColIndex(nonHdl);
  const genderIndex = input.gender === 'female' ? 0 : 1;
  const smokerIndex = input.smoker ? 1 : 0;

  if (input.mode === 'SCORE2') {
    const ageBand = getScore2AgeBandIndex(input.age);
    const matrix = SCORE2_MATRICES[genderIndex][smokerIndex][ageBand] as RiskMatrix;
    const rawRisk = matrix[sbpRow][nonHdlCol];
    return {
      riskPercent: rawRisk,
      hasDiabetesMultiplier: false,
      rawRiskPercent: rawRisk,
    };
  }

  // SCORE2-OP
  const ageBand = getScore2OpAgeBandIndex(input.age);
  const matrix = SCORE2_OP_MATRICES[genderIndex][smokerIndex][ageBand] as RiskMatrix;
  let rawRisk = matrix[sbpRow][nonHdlCol];
  const hasDiabetes = input.diabetes === true;
  const riskPercent = hasDiabetes
    ? Math.min(100, Math.round(rawRisk * SCORE2_OP_DIABETES_MULTIPLIER * 10) / 10)
    : rawRisk;

  return {
    riskPercent,
    hasDiabetesMultiplier: hasDiabetes,
    rawRiskPercent: rawRisk,
  };
}

/**
 * Get risk category from percentage (for display).
 * Thresholds for high-risk region: Low <5%, Moderate 5-<10%, High 10-<15%, Very High ≥15% (for 50-69);
 * Simplified: Low <5%, Moderate 5-<10%, High 10-<20%, Very High ≥20%
 */
export function getRiskCategory(riskPercent: number): 'low' | 'moderate' | 'high' | 'veryHigh' {
  if (riskPercent < 5) return 'low';
  if (riskPercent < 10) return 'moderate';
  if (riskPercent < 20) return 'high';
  return 'veryHigh';
}
